import { useCallback, useEffect, useState } from 'react'

import { getOrganizationDetails } from '@/organizations/fetch/get-organization-details'
import type { GetOrganizationWithDetailsResponse } from '@/organizations/schemas'

type UseOrganizationDetailsResult = {
  data: GetOrganizationWithDetailsResponse | undefined
  isLoading: boolean
  isError: boolean
  error: unknown
  refetch: () => void
}

/**
 * Fetches the full organization details (org, users, invites) when enabled.
 *
 * Admin-only. Used by the admin dashboard user list and the account-menu org roster.
 * Returns an RTK-Query-compatible shape so callers read data/isLoading/refetch the same way.
 */
export function useOrganizationDetails({ enabled }: { enabled: boolean }): UseOrganizationDetailsResult {
  const [data, setData] = useState<GetOrganizationWithDetailsResponse | undefined>(undefined)
  const [isLoading, setIsLoading] = useState(enabled)
  const [isError, setIsError] = useState(false)
  const [error, setError] = useState<unknown>(null)

  const refetch = useCallback(async () => {
    setIsLoading(true)
    setIsError(false)
    setError(null)
    try {
      setData(await getOrganizationDetails())
    } catch (e) {
      setIsError(true)
      setError(e)
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    if (!enabled) {
      setIsLoading(false)
      return
    }
    void refetch()
  }, [enabled, refetch])

  return { data, isLoading, isError, error, refetch }
}
